import { type TLlmExtractOfferContact, useGenerateOffersFromIA } from "@api";
import type { ICreateWithAIGeneration } from "@complex/CreateWithAIModal";
import type { OfferKind } from "@root/utils/enums/offer";
import { formatInputPhoneNumber } from "@root/utils/phoneLib";
import { useMemo, useState } from "react";
import type { FieldValues } from "react-hook-form";

export const formatContactFromExtractedOffer = (
  contact: TLlmExtractOfferContact | undefined,
  phoneCode: string
): FieldValues | undefined => {
  if (!contact) {
    return undefined;
  }
  const { firstName, lastName } = contact.firstName || contact.lastName ? contact : splitContactName(contact.name);
  const email = contact.email ?? contact.mail;
  if (!firstName && !lastName && !email && !contact.phone && !contact.job && !contact.description) {
    return undefined;
  }
  return {
    ...(contact._id ? { _id: contact._id } : {}),
    firstName,
    lastName,
    job: contact.job,
    email,
    phone: contact.phone ? formatInputPhoneNumber(contact.phone, phoneCode) : undefined,
    description: contact.description,
  };
};

const splitContactName = (name?: string): { firstName?: string; lastName?: string } => {
  if (!name) {
    return {};
  }
  const [firstName, ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") || undefined };
};

/**
 * Pilote une génération IA et en dérive l'état consommable par `CreateWithAIModal`.
 *
 * Commun à tous les domaines : offre, candidature (qui se rattache à une offre), offre de
 * service… Seuls `kind`, les libellés et le schéma changent d'un appelant à l'autre.
 */
export const useAIGeneration = <TRaw, TForm = TRaw>(kind: OfferKind, parseItem?: (item: TRaw) => TForm) => {
  const { generatedOffers, generate, cancelGeneration } = useGenerateOffersFromIA<TRaw>(kind);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(false);

  const total = generatedOffers.length;
  const settledCount = generatedOffers.filter((offer) => offer.status !== "pending").length;

  // Référence stable une fois la génération terminée : `generatedOffers` ne bouge plus,
  // donc la navigation multi-items n'est pas réinitialisée pendant l'édition.
  const items = useMemo(
    () =>
      generatedOffers.flatMap((offer) => {
        if (offer.status !== "fulfilled") {
          return [];
        }
        const raw = offer.value;
        return [(parseItem ? parseItem(raw) : (raw as unknown as TForm)) as TForm];
      }),
    [generatedOffers, parseItem]
  );

  const generation: ICreateWithAIGeneration<TForm> = {
    items,
    // `generate` rend la main dès la fin du parse, les extractions continuent en tâche de fond :
    // la génération n'est finie que lorsque tous les items annoncés ont abouti ou échoué.
    running: parsing || (total > 0 && settledCount < total),
    count: settledCount,
    total,
    error,
  };

  const handleGenerate = async (text: string) => {
    setParsing(true);
    setError(false);
    try {
      await generate(text);
    } catch {
      // Seul le parse peut échouer ici : une extraction ratée devient un item `rejected`.
      setError(true);
    } finally {
      setParsing(false);
    }
  };

  /** Annule la génération en cours et efface l'erreur. À appeler à la fermeture. */
  const reset = () => {
    cancelGeneration();
    setError(false);
  };

  return { generation, handleGenerate, reset };
};
